import { useState, useEffect } from 'react';
import { useFamilyStore } from '../stores/familyStore';
import { useEncryptionContext } from '../contexts/EncryptionContext';

interface ChildrenNameMap {
  [childId: string]: string;
}

/**
 * Hook to get decrypted child names by ID.
 * Uses data from familyStore + E2EE decryption.
 */
export function useChildrenNames() {
  const { children } = useFamilyStore();
  const { decryptIfNeeded, isReady } = useEncryptionContext();
  const [childrenNames, setChildrenNames] = useState<ChildrenNameMap>({});

  useEffect(() => {
    if (!children || children.length === 0) return;

    let cancelled = false;

    async function buildMap() {
      const namesMap: ChildrenNameMap = {};
      for (const child of children) {
        namesMap[child.id] = isReady
          ? await decryptIfNeeded(child.name)
          : child.name;
      }
      if (!cancelled) setChildrenNames(namesMap);
    }

    buildMap();
    return () => { cancelled = true };
  }, [children, isReady, decryptIfNeeded]);

  const getChildName = (childId?: string): string => {
    if (!childId) return 'child';
    return childrenNames[childId] || 'child';
  };

  return {
    childrenNames,
    getChildName,
    isLoaded: Object.keys(childrenNames).length > 0
  };
}
